import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Lock, Copy, CheckCircle2, AlertTriangle, FileText, Settings, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { productConfigurationService, toolListService, toolListLineService, workCycleService, controlPlanService, attachmentService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_BADGE = { bozza: 'bg-amber-500 text-white', approvata: 'bg-emerald-500 text-white', obsoleta: 'bg-slate-400 text-white' };
const STATUS_LABEL = { bozza: 'Bozza', approvata: 'Approvata', obsoleta: 'Obsoleta' };

export default function ConfigurationDetailPage() {
  const { id } = useParams();
  const [config, setConfig] = useState(null);
  const [toolList, setToolList] = useState(null);
  const [toolListLines, setToolListLines] = useState([]);
  const [workCycles, setWorkCycles] = useState([]);
  const [controlPlans, setControlPlans] = useState([]);
  const [productToolLists, setProductToolLists] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [lineDialogOpen, setLineDialogOpen] = useState(false);
  const { hasRole } = useAuth();

  const [editForm, setEditForm] = useState({});
  const [lineForm, setLineForm] = useState({ tool_code: '', description: '', phase: '', height: '', radius: '', notes: '' });
  const [editingLineId, setEditingLineId] = useState(null);
  const [editLineForm, setEditLineForm] = useState({});

  useEffect(() => { loadConfig(); }, [id]);

  const loadConfig = async () => {
    try {
      const res = await productConfigurationService.getById(id);
      setConfig(res.data);
      // Load related data for product
      const [wcRes, cpRes, tlRes, attRes] = await Promise.all([
        workCycleService.getAll({ product_id: res.data.product_id }).catch(() => ({ data: [] })),
        controlPlanService.getAll({ product_id: res.data.product_id }).catch(() => ({ data: [] })),
        toolListService.getAll({ product_id: res.data.product_id }),
        attachmentService.getAll({ entity_type: 'configuration', entity_id: id }),
      ]);
      // work_cycles are by revision, flatten all
      setWorkCycles(wcRes.data || []);
      setControlPlans(cpRes.data || []);
      setProductToolLists(tlRes.data || []);
      setAttachments(attRes.data || []);
      // Load linked tool list
      if (res.data.tool_list_id) {
        const tlDetailRes = await toolListService.getById(res.data.tool_list_id);
        setToolList(tlDetailRes.data);
        setToolListLines(tlDetailRes.data.lines || []);
      }
    } catch { toast.error('Errore caricamento configurazione'); }
    finally { setLoading(false); }
  };

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  const showConfirm = (title, message, onConfirm) => setConfirmDialog({ open: true, title, message, onConfirm });

  const handleApprove = () => {
    showConfirm('Approva Configurazione', 'Approvare questa configurazione? Una volta approvata non sarà più modificabile.', async () => {
      try {
        await productConfigurationService.update(id, { status: 'approvata' });
        toast.success('Configurazione approvata');
        loadConfig();
      } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    });
  };

  const handleObsolete = () => {
    showConfirm('Rendi Obsoleta', 'Rendere obsoleta questa configurazione? Non potrà più essere utilizzata in produzione.', async () => {
      try {
        await productConfigurationService.update(id, { status: 'obsoleta' });
        toast.success('Configurazione resa obsoleta');
        loadConfig();
      } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    });
  };

  const handleSaveEdit = async () => {
    try {
      await productConfigurationService.update(id, editForm);
      toast.success('Configurazione aggiornata');
      setEditOpen(false);
      loadConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleAddLine = async () => {
    if (!toolList) return;
    try {
      await toolListLineService.create({ tool_list_id: toolList.id, ...lineForm });
      toast.success('Riga aggiunta');
      setLineDialogOpen(false);
      setLineForm({ tool_code: '', description: '', phase: '', height: '', radius: '', notes: '' });
      loadConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteLine = async (lineId) => {
    showConfirm('Elimina Riga', 'Eliminare questa riga dalla lista utensili?', async () => {
      try {
        await toolListLineService.delete(lineId);
        toast.success('Riga eliminata');
        loadConfig();
      } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    });
  };

  const handleSaveLineEdit = async (lineId) => {
    try {
      await toolListLineService.update(lineId, editLineForm);
      toast.success('Riga aggiornata');
      setEditingLineId(null);
      loadConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const startEditLine = (line) => {
    setEditingLineId(line.id);
    setEditLineForm({ tool_code: line.tool_code || '', description: line.description || '', phase: line.phase || '', height: line.height || '', radius: line.radius || '', notes: line.notes || '' });
  };

  const handleNewToolListVersion = async () => {
    if (!toolList) return;
    showConfirm('Nuova Versione', 'Creare una nuova versione modificabile da questa lista approvata?', async () => {
      try {
        const res = await toolListService.newVersion(toolList.id);
        toast.success(`Nuova versione ${res.data.tool_list_code} creata in bozza`);
        await productConfigurationService.update(id, { tool_list_id: res.data.id });
        loadConfig();
      } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    });
  };

  const handleApproveToolList = async () => {
    if (!toolList) return;
    showConfirm('Approva Lista Utensili', 'Approvare questa lista utensili? Non sarà più modificabile.', async () => {
      try {
        await toolListService.update(toolList.id, { status: 'approvata' });
        toast.success('Lista utensili approvata');
        loadConfig();
      } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    });
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  if (!config) return <div className="text-center py-20 text-slate-500">Configurazione non trovata</div>;

  const isFrozen = config.status === 'approvata' || config.status === 'obsoleta';

  return (
    <div className="space-y-6" data-testid="configuration-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/products/${config.product_id}`}>
            <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Prodotto</Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 font-mono" data-testid="config-code">{config.configuration_code}</h1>
              <Badge className={STATUS_BADGE[config.status]}>{STATUS_LABEL[config.status]}</Badge>
              {isFrozen && <Lock className="w-4 h-4 text-slate-400" />}
            </div>
            <p className="text-sm text-slate-500 mt-1">{config.product_code} - {config.product_name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {config.status === 'bozza' && hasRole('admin', 'quality') && (
            <>
              <Button variant="outline" onClick={() => { setEditForm({ drawing_revision: config.drawing_revision || '', description: config.description || '', work_cycle_id: config.work_cycle_id || '', control_plan_id: config.control_plan_id || '', tool_list_id: config.tool_list_id || '', process_flow: config.process_flow || '' }); setEditOpen(true); }} data-testid="edit-config-btn">
                <Edit2 className="w-4 h-4 mr-2" />Modifica
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} data-testid="approve-config-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />Approva
              </Button>
            </>
          )}
          {config.status === 'approvata' && hasRole('admin', 'quality') && (
            <Button variant="outline" onClick={handleObsolete} data-testid="obsolete-config-btn">
              <AlertTriangle className="w-4 h-4 mr-2" />Rendi Obsoleta
            </Button>
          )}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Revisione Disegno</CardTitle></CardHeader>
          <CardContent><p className="text-lg font-semibold">{config.drawing_revision || '-'}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Ciclo di Lavoro</CardTitle></CardHeader>
          <CardContent><p className="text-lg font-semibold">{config.work_cycle_code || '-'}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Piano di Controllo</CardTitle></CardHeader>
          <CardContent><p className="text-lg font-semibold">{config.control_plan_code || '-'}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Lista Utensili</CardTitle></CardHeader>
          <CardContent><p className="text-lg font-semibold">{config.tool_list_code || '-'}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Disegno</CardTitle></CardHeader>
          <CardContent>
            {config.drawing_file_name ? (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api${config.drawing_file_url}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-sm">{config.drawing_file_name}</a>
            ) : <p className="text-sm text-slate-400">Nessun disegno</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Flusso di Processo</CardTitle></CardHeader>
          <CardContent><p className="text-sm">{config.process_flow || '-'}</p></CardContent>
        </Card>
      </div>

      {config.description && (
        <Card>
          <CardHeader><CardTitle className="text-base">Descrizione</CardTitle></CardHeader>
          <CardContent><p className="text-slate-700">{config.description}</p></CardContent>
        </Card>
      )}

      {/* Tool List Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base">Lista Utensili{toolList ? ` - ${toolList.tool_list_code} (V${toolList.version})` : ''}</CardTitle>
              {toolList && (
                <Badge className={toolList.status === 'approvata' ? 'bg-emerald-500 text-white' : toolList.status === 'obsoleta' ? 'bg-slate-400 text-white' : 'bg-amber-500 text-white'}>
                  {toolList.status === 'approvata' ? 'Approvata' : toolList.status === 'obsoleta' ? 'Obsoleta' : 'Bozza'}
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              {toolList && toolList.status === 'bozza' && hasRole('admin', 'quality', 'production') && (
                <>
                  <Button size="sm" variant="outline" onClick={handleApproveToolList} data-testid="approve-toollist-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" />Approva Lista
                  </Button>
                  <Button size="sm" onClick={() => setLineDialogOpen(true)} data-testid="add-tool-line-btn">
                    <Plus className="w-4 h-4 mr-1" />Aggiungi Utensile
                  </Button>
                </>
              )}
              {toolList && toolList.status === 'approvata' && hasRole('admin', 'quality', 'production') && (
                <Button size="sm" variant="outline" onClick={handleNewToolListVersion} data-testid="new-version-toollist-btn">
                  <Copy className="w-4 h-4 mr-1 text-blue-600" />Nuova Versione (Modifica)
                </Button>
              )}
            </div>
          </div>
          {toolList && toolList.status === 'approvata' && (
            <p className="text-xs text-slate-500 mt-2">Lista approvata e congelata. Per modificarla, crea una nuova versione.</p>
          )}
        </CardHeader>
        <CardContent>
          {!toolList ? (
            <p className="text-sm text-slate-400">Nessuna lista utensili collegata a questa configurazione. Collegane una dalla modifica configurazione.</p>
          ) : toolListLines.length === 0 ? (
            <p className="text-sm text-slate-400">Lista vuota - aggiungi utensili con il pulsante sopra</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Codice Utensile</th>
                    <th>Descrizione</th>
                    <th>Fase</th>
                    <th>Altezza</th>
                    <th>Raggio</th>
                    <th>Note</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {toolListLines.map(line => (
                    editingLineId === line.id ? (
                      <tr key={line.id} className="bg-blue-50">
                        <td><Input className="h-8 text-sm" value={editLineForm.tool_code} onChange={e => setEditLineForm(f => ({ ...f, tool_code: e.target.value }))} data-testid="edit-line-tool-code" /></td>
                        <td><Input className="h-8 text-sm" value={editLineForm.description} onChange={e => setEditLineForm(f => ({ ...f, description: e.target.value }))} data-testid="edit-line-description" /></td>
                        <td><Input className="h-8 text-sm" value={editLineForm.phase} onChange={e => setEditLineForm(f => ({ ...f, phase: e.target.value }))} /></td>
                        <td><Input className="h-8 text-sm w-20" value={editLineForm.height} onChange={e => setEditLineForm(f => ({ ...f, height: e.target.value }))} /></td>
                        <td><Input className="h-8 text-sm w-20" value={editLineForm.radius} onChange={e => setEditLineForm(f => ({ ...f, radius: e.target.value }))} /></td>
                        <td><Input className="h-8 text-sm" value={editLineForm.notes} onChange={e => setEditLineForm(f => ({ ...f, notes: e.target.value }))} /></td>
                        <td>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleSaveLineEdit(line.id)} data-testid="save-line-edit-btn"><Save className="w-4 h-4 text-emerald-600" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditingLineId(null)}><X className="w-4 h-4 text-slate-500" /></Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={line.id} className={toolList.status === 'bozza' ? 'cursor-pointer hover:bg-slate-50' : ''} onClick={() => { if (toolList.status === 'bozza' && hasRole('admin', 'quality', 'production')) startEditLine(line); }}>
                        <td className="font-mono">{line.tool_code || '-'}</td>
                        <td>{line.description}</td>
                        <td>{line.phase || '-'}</td>
                        <td>{line.height || '-'}</td>
                        <td>{line.radius || '-'}</td>
                        <td className="max-w-[150px] truncate">{line.notes || '-'}</td>
                        <td onClick={e => e.stopPropagation()}>
                          {toolList.status === 'bozza' && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEditLine(line)} data-testid={`edit-line-${line.id}`}>
                                <Edit2 className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteLine(line.id)}>
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attachments */}
      <Card>
        <CardHeader><CardTitle className="text-base">Documenti Allegati ({attachments.length})</CardTitle></CardHeader>
        <CardContent>
          {attachments.length === 0 ? (
            <p className="text-sm text-slate-400">Nessun documento allegato</p>
          ) : (
            <div className="space-y-2">
              {attachments.map(att => (
                <div key={att.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api${att.file_url}`} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:underline">{att.file_name}</a>
                      {att.description && <p className="text-xs text-slate-500">{att.description}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Modifica Configurazione</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Revisione Disegno</Label>
              <Input value={editForm.drawing_revision || ''} onChange={e => setEditForm(f => ({ ...f, drawing_revision: e.target.value }))} data-testid="edit-drawing-revision" />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea value={editForm.description || ''} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Ciclo di Lavoro</Label>
              <Select value={editForm.work_cycle_id || 'none'} onValueChange={v => setEditForm(f => ({ ...f, work_cycle_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {workCycles.map(wc => <SelectItem key={wc.id} value={wc.id}>{wc.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Piano di Controllo</Label>
              <Select value={editForm.control_plan_id || 'none'} onValueChange={v => setEditForm(f => ({ ...f, control_plan_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {controlPlans.map(cp => <SelectItem key={cp.id} value={cp.id}>{cp.plan_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lista Utensili</Label>
              <Select value={editForm.tool_list_id || 'none'} onValueChange={v => setEditForm(f => ({ ...f, tool_list_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna</SelectItem>
                  {productToolLists.map(tl => <SelectItem key={tl.id} value={tl.id}>{`${tl.tool_list_code} (V${tl.version})`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Flusso di Processo</Label>
              <Textarea value={editForm.process_flow || ''} onChange={e => setEditForm(f => ({ ...f, process_flow: e.target.value }))} rows={3} placeholder="Descrizione del flusso di processo..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveEdit} data-testid="save-config-edit-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tool Line Dialog */}
      <Dialog open={lineDialogOpen} onOpenChange={setLineDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Aggiungi Utensile alla Lista</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Codice Utensile</Label>
                <Input value={lineForm.tool_code} onChange={e => setLineForm(f => ({ ...f, tool_code: e.target.value }))} data-testid="line-tool-code" />
              </div>
              <div className="space-y-2">
                <Label>Fase</Label>
                <Input value={lineForm.phase} onChange={e => setLineForm(f => ({ ...f, phase: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Input value={lineForm.description} onChange={e => setLineForm(f => ({ ...f, description: e.target.value }))} required data-testid="line-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Altezza</Label>
                <Input value={lineForm.height} onChange={e => setLineForm(f => ({ ...f, height: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Raggio</Label>
                <Input value={lineForm.radius} onChange={e => setLineForm(f => ({ ...f, radius: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={lineForm.notes} onChange={e => setLineForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLineDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleAddLine} data-testid="save-tool-line-btn">Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog (replaces window.confirm) */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => { if (!open) setConfirmDialog(c => ({ ...c, open: false })); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 py-2">{confirmDialog.message}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(c => ({ ...c, open: false }))} data-testid="confirm-cancel-btn">Annulla</Button>
            <Button onClick={async () => { setConfirmDialog(c => ({ ...c, open: false })); if (confirmDialog.onConfirm) await confirmDialog.onConfirm(); }} data-testid="confirm-ok-btn">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
